import { Component, OnInit, OnDestroy } from '@angular/core';
import { Tenant } from '../../core/data-models/tenant';
import { PlatformUserService } from '../../core/services/platform-user.service';
import { PlatformUser } from '../../core/data-models/platform-user';
import { Entity } from '../../core/data-models/Entity';
import { LanguageService } from '../../core/services/language.service';
import { Subscription, Subject, merge, of, firstValueFrom } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CreditBalanceDialogComponent } from '../../shared/credit-balance-dialog/credit-balance-dialog.component';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { ErrorHandlerService } from '../../core/services/error-handler.service';
import { CrashReportingService } from '../../core/services/crash-reporting.service';
import { GeneralService } from '../../core/services/general.service';
import { GlobalsService } from 'src/app/core/services/globals.service';
import { ModuleName } from 'src/app/shared/enums/ModuleName';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';

import { ZenSnackbarService, ZenSnackBarTypes } from 'src/app/shared/Theme/services/zen-snackbar.service';
import { TableOptions } from 'src/app/core/data-models/table-options';
import { EssentialObject, EssentialObjectService } from 'src/app/core/services/essential-object.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { products } from 'src/app/core/data-models/company-permission';
import { UserRoles } from 'src/app/core/user-roles';
import { Router } from '@angular/router';
import { AccountOverviewService } from './data-access/account-overview.service';
import { BalanceTransaction, EntityBalance, TenantBalances } from './data-access/account-balance.models';
import { PRODUCT_CATALOG, ProductCatalogEntry } from './data-access/product-catalog';
import { SimpleTableOptions } from 'src/app/shared/Theme/components/table/table.component';
import { BaseComponent } from '@ex/module/core/domain/base-component';

/** t-table paginates client-side at this size; it is not exported by the component. */
const TEMP_TABLE_PAGE_SIZE = 10;

@Component({
  selector: 'app-account-overview',
  templateUrl: './account-overview.component.html',
  styleUrls: ['./account-overview.component.scss']
})
export class AccountOverviewComponent extends BaseComponent implements OnInit, OnDestroy {
  ModuleNames = ModuleName
  private subscriptions = new Subscription();

  /**
   * Reloads asked for by the page itself, rather than by a company switch. Merged into
   * the same stream as the switches so switchMap can cancel one against the other.
   */
  private reload$ = new Subject<void>();

  productCatalog = PRODUCT_CATALOG;

  selectedEntity: Entity;
  tenant: Tenant;
  companies: Entity[] = [];
  user: PlatformUser;
  language: string = 'EN';

  get isSuperAdmin(): boolean {
    return this.user?.role == UserRoles.SuperAdmin;
  }

  balances: TenantBalances;
  selected: EntityBalance;

  /** Units typed into the assign input, per product key. Cleared on every balance reload. */
  unitsToAssign: { [productKey: string]: number } = {};

  /** app-table config for the product rows. Shape depends only on the user's role. */
  productTableOptions: SimpleTableOptions;
  private productTableBuiltForSuperAdmin: boolean = null;

  historyModal = false;
  historyType: string;
  selectedProduct: ProductCatalogEntry;
  options: TableOptions;
  colors;

  constructor(public matConfirmDialog: MatDialog, private zenSnackBarService: ZenSnackbarService,
    private platformUserService: PlatformUserService, public loaderService: LoaderService,
    private languageService: LanguageService, public essentialObjectService: EssentialObjectService,
    private accountOverviewService: AccountOverviewService, public matDDialog: MatDialog, private globals: GlobalsService,
    private errorHandlerService: ErrorHandlerService, private crashReportingService: CrashReportingService
    , private translationPipe: TranslatePipe, private generalService: GeneralService, private router: Router) {
    super();
    this.colors = globals.basicColors;
    this.loaderService.displayLoader();
  }

  ngOnInit(): void {
    const companySelection = this.essentialObjectService.essentialObject.pipe(
      filter((object: EssentialObject) => !!object?.user && !!object?.tenant?.key
        && !!object?.selectedEntity?.key && !!object?.companies?.length),
      tap((object: EssentialObject) => {
        if (object.language)
          this.language = object.language;

        this.user = object.user;
        this.tenant = object.tenant;
        this.companies = object.companies;
        this.selectedEntity = object.selectedEntity;
        this.syncProductTable();
      }),
      filter((object: EssentialObject) => this.enforceCompanyPermission(object)),
      filter((object: EssentialObject) => object.tenant.setup == true),
      distinctUntilChanged((previous: EssentialObject, current: EssentialObject) =>
        previous.tenant.key == current.tenant.key && previous.selectedEntity.key == current.selectedEntity.key),
      map(() => undefined)
    );

    // Every reload goes through this one switchMap. A reload the page asked for is
    // otherwise free to land after a company switch and overwrite the new company's
    // balances with the old company's — and, since the request is scoped by
    // companyKey, changeSelected() then finds nothing and the table disappears.
    this.subscriptions.add(merge(companySelection, this.reload$).pipe(
      switchMap(() => this.loadBalances())
    ).subscribe());

    this.globals.innerRoute = true;
  }

  ngOnDestroy() {
    // Deliberate, not a leftover: innerRoute drives content-container's 'inner' class,
    // which is what keeps the side navbar visible. Leaving it true on the way out is the
    // fix from "inner route issue on account overview" (bd51e2932) — routes that want it
    // false set it themselves in their own ngOnInit. Do not "clean this up" to false.
    this.globals.innerRoute = true;
    this.reload$.complete();
    this.subscriptions.unsubscribe();
  }

  /**
   * AccountOverviewPermissionGuard covers route entry. This covers switching company
   * from the header, which can land on one the user has no Overview permission for —
   * the header's dropdown lists companies with any permission, not Overview specifically.
   *
   * Reads the permissions already on the essential object, so it costs no request.
   *
   * Returns false once the redirect is under way, so the caller drops the emission
   * instead of loading balances for a company the user is being navigated away from.
   */
  private enforceCompanyPermission(object: EssentialObject): boolean {
    const permissions = object.user.ModulePermissions || object.user.CompanyPermissions;
    const isSuperAdmin = object.user.role == UserRoles.SuperAdmin;

    if (this.generalService.hasPermission(object.selectedEntity.key, products.Overview, permissions, isSuperAdmin))
      return true;

    this.router.navigateByUrl(this.generalService.resolveRoute(object.selectedEntity.key, permissions));
    return false;
  }

  /**
   * Column names stay translation keys: app-table localizes them itself against
   * [language], so a language change needs no rebuild. Only the super-admin-only
   * Assign column changes the table's shape, and rebuilding on every emission would
   * re-run app-table's options setter for nothing.
   */
  private syncProductTable() {
    if (this.productTableBuiltForSuperAdmin === this.isSuperAdmin)
      return;

    this.productTableBuiltForSuperAdmin = this.isSuperAdmin;
    this.productTableOptions = {
      data: this.productCatalog,
      columns: [
        { field: 'product', name: 'txtProduct' },
        { field: 'currentBalance', name: 'txtCurrentBalance' },
        { field: 'usedBalance', name: 'txtUsedUnits' },
        ...(this.isSuperAdmin ? [{ field: 'assign', name: 'txtAssignUnits' }] : []),
        // Action column; app-table renders an empty header for a blank name.
        { field: 'history', name: '' }
      ]
    };
  }

  private loadBalances() {
    return this.accountOverviewService.fetchBalances(this.tenant.key, this.companies.map(c => c.key), this.selectedEntity.key).pipe(
      tap((balances: TenantBalances) => {
        if (balances) {
          this.balances = balances;
          // Cleared to null, not by emptying the map: z-input's writeValue ignores
          // undefined, so a missing key leaves the typed number on screen.
          this.unitsToAssign = Object.fromEntries(this.productCatalog.map(product => [product.key, null]));
        }
        this.changeSelected(this.selectedEntity.key);
        this.loaderService.hideLoader();
      }),
      catchError((ex) => {
        this.loaderService.hideLoader();
        this.reportPageError(1608, ex);
        return of(null);
      })
    );
  }

  private changeSelected(key: string) {
    this.selected = this.balances?.entities.find(entity => entity.key == key);
  }

  getReadableDate(date: number) {
    if (date == -1)
      return this.translationPipe.transform("txtNoExpiry", this.language, this.ModuleNames.Common);

    let theDate = new Date(date * 1000);
    return theDate.getDate() + '/' + (theDate.getMonth() + 1) + '/' + theDate.getFullYear();
  }

  purchaseHistory(historyType: string, product: ProductCatalogEntry) {
    this.selectedProduct = product;
    this.historyType = historyType;

    const history: BalanceTransaction[] = historyType == 'Entity'
      ? this.accountOverviewService.entityHistory(this.selected, product.key)
      : this.accountOverviewService.tenantHistory(this.balances, product.key);

    this.options = {
      data: history,
      columns: [
        { field: 'first', title: this.localize('txtPurchasedUnits', this.language, ModuleName.Common) },
        { field: 'second', title: this.localize(historyType == 'Entity' ? 'txtAllocationDate' : 'txtDateofPurchase', this.language, ModuleName.Common) },
        { field: 'third', title: this.localize('txtExpiryDate', this.language, ModuleName.Common) },
        // Trailing spacer: t-table spreads columns evenly, so without it the
        // expiry date stretches across the rest of the drawer. No cell template.
        { field: 'fourth', title: '' },
      ],
      selectionKey: '',
      colors: this.colors,
      searchText: this.localize('txt_search', this.language, ModuleName.Common),
      whiteBackground: true,
      // t-table paginates client-side at 10 a page and renders the paginator regardless,
      // so a two-row history came with a lone "1" button and a page-size picker under it.
      hidePagination: history.length <= TEMP_TABLE_PAGE_SIZE
    };
    this.historyModal = true;
  }

  /**
   * Opens straight from the balances already loaded — the sidebar is rendering the same
   * tenant-wide figures. This used to wait on a tenantCompanyBalance round trip first,
   * with no loader, so the page sat dead for the length of a cloud-function call.
   *
   * A stale figure is acceptable here: this dialog only *requests* units from an admin.
   * addUnits() is the path that moves units, and that one does reload.
   */
  requestMoreUnits(survey) {
    this.matDDialog.open(CreditBalanceDialogComponent, {
      data: {
        tenantKey: this.tenant.key, entity: this.selectedEntity, creditType: survey, language: this.language, currentBalance: this.balances
        , user: this.user, tenant: this.tenant, submittedFrom: 'ACCOUNTOVERVIEW', requestType: 'MAINREQUEST'
      }, autoFocus: false,
    });
  }

  async addUnits(entity: EntityBalance, unitsType: string, units: number) {
    try {
      if (!(units >= 1))
        return this.zenSnackBarService.snackBar({ message: this.localize('noUnits', this.language, ModuleName.Common), type: ZenSnackBarTypes.Error });

      const confirmed = this.user?.doNotShowAllocationConfirmation || await this.confirmAllocation(units);
      if (!confirmed)
        return;

      this.loaderService.displayLoader();

      const result = await firstValueFrom(this.accountOverviewService.allocateUnits({
        entityKey: entity.key, entity: entity, user: this.user, tenantKey: this.tenant.key,
        creditType: unitsType.toUpperCase(), creditUnits: units
      }));

      if (result.success) {
        this.crashReportingService.submitLogCode(15);
        this.reload$.next();
        return;
      }

      this.loaderService.hideLoader();

      if (result.insufficientCredits) {
        this.matDDialog.open(CreditBalanceDialogComponent, {
          data: {
            tenantKey: this.tenant.key, entity: this.selectedEntity, creditType: 'MAINBALANCE', language: this.language, currentBalance: result.mainBalance
            , user: this.user, tenant: this.tenant, submittedFrom: 'ACCOUNTOVERVIEW'
          }, autoFocus: false,
        });
      }
    } catch (ex) {
      // Awaited end to end, so this covers the allocation request and the dialogs it
      // opens. Subscribing instead left the callback throwing past this catch.
      this.loaderService.hideLoader();
      this.reportPageError(1611, ex);
    }
  }

  async confirmAllocation(units): Promise<boolean> {
    try {
      const confirmationDialog = this.matConfirmDialog.open(ConfirmationDialogComponent, {
        data: {
          message: this.localize('txt_unit_allocation_confirmation', this.language, ModuleName.Common).replace('#UNITS#', units),
          subject: this.localize('txt_allocation_confirmation', this.language, ModuleName.Common),
          dialogType: 'info-with-check',
          checkboxText: this.localize('txtDontShow', this.language, ModuleName.Common),
          actionButtonText: this.localize('txt_okay_action', this.language, ModuleName.Common),
          cancelButtonText: this.localize('btn_cancel', this.language, ModuleName.Common)
        }, autoFocus: false
      });

      const result = await firstValueFrom(confirmationDialog.afterClosed());
      if (result?.reply != 'YES')
        return false;

      if (result.isChecked) {
        this.user.doNotShowAllocationConfirmation = true;
        this.platformUserService.updatePlatformUser(this.user).catch(() => { });
      }
      return true;
    } catch (ex) {
      this.reportPageError(1612, ex);
      return false;
    }
  }

  extractNameLanguage(eG: any) {
    return this.languageService.extractNameLanguage(eG, this.language);
  }


  private reportPageError(code: number, ex: any) {
    this.errorHandlerService.submitErrorMessage(code, ex?.toString(), this.tenant?.key, this.tenant?.name, this.selectedEntity?.key,
      this.ExtractPipe.transform(this.selectedEntity?.name, this.language), this.user?.firstName + ' ' + this.user?.lastName + ' (' + this.user?.email + ')');
  }

}
